import { useState } from "react";
import { Link } from "react-router-dom";

const faqData = [
  {
    q: "What is pandabuyspreadsheet?",
    a: "Pandabuyspreadsheet is a curated product directory that organizes thousands of fashion, lifestyle, and accessory items into structured, easy-to-browse collections. Instead of searching manually across scattered sources, users access organized spreadsheets with direct links to products across 14 categories including shoes, hoodies, t-shirts, jackets, accessories, and more.",
  },
  {
    q: "Is the pandabuyspreadsheet updated regularly?",
    a: "Yes. The pandabuyspreadsheet directory is refreshed daily to ensure all product listings, prices, and availability information remain current. New trending items and community discoveries are continuously added to keep the collections relevant and valuable.",
  },
  {
    q: "How do pandabuy spreadsheets work?",
    a: "Each pandabuyspreadsheet organizes products into clearly labeled categories with direct access links. You simply browse a category — like Shoes or Jackets — and you are taken to a dedicated product page on our main website where you can explore detailed listings, compare options, and access the latest curated finds.",
  },
  {
    q: "Is it safe to use pandabuyspreadsheet?",
    a: "Absolutely. Every product and seller linked through pandabuyspreadsheet undergoes verification checks. The curation team maintains strict quality standards, and any product that falls below the quality threshold is promptly removed. We prioritize safe, reliable access to curated product lists.",
  },
  {
    q: "How do I find the best products on pandabuyspreadsheet?",
    a: "Start by browsing the category that interests you most. The pandabuyspreadsheet system highlights trending and highly-rated products within each category, making it easy to spot popular items. You can also check our regularly updated blog guides for curated top-pick roundups and insider shopping tips.",
  },
];

export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <section className="w-full bg-background-50 px-6 md:px-10 py-16 md:py-24">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-4xl font-heading text-foreground-900">
            Frequently Asked Questions
          </h2>
          <p className="mt-3 text-sm text-foreground-500">
            Everything you need to know about{" "}
            <strong>pandabuyspreadsheet</strong>
          </p>
        </div>

        <div className="space-y-3">
          {faqData.map((item, idx) => (
            <div
              key={idx}
              className="bg-background-100 rounded-xl overflow-hidden transition-colors"
            >
              <button
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-200/70 transition-colors"
              >
                <span className="text-sm text-foreground-900 pr-4">
                  {item.q}
                </span>
                <i
                  className={`ri-add-line text-foreground-500 text-lg transition-transform duration-300 flex-shrink-0 ${
                    openIdx === idx ? "rotate-45" : ""
                  }`}
                ></i>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIdx === idx ? "max-h-96 pb-5 px-5" : "max-h-0"
                }`}
              >
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {item.a}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-sm text-foreground-500 mb-4">
            Still have questions? Explore our detailed guide
          </p>
          <Link
            to="/article/pandabuyspreadsheet-guide"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Read Full Guide
            <i className="ri-arrow-right-line text-base"></i>
          </Link>
        </div>
      </div>

      {/* FAQ Page structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqData.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.a,
              },
            })),
          }),
        }}
      />
    </section>
  );
}